import type { EnvironmentType } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import type { ActionEvent, AnalyticActionDispatcher, AnalyticTrackedActionDispatcher } from "~/analytics/Analytics";
import { Action, useAnalyticActionDispatch, useAnalyticTrackedActionDispatch } from "~/analytics/Analytics";
import { useEphemeralEnvironmentsEnabled } from "~/areas/projects/components/EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import { repository } from "~/clientInstance";
import type { DataBaseComponentState } from "~/components/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent/DataBaseComponent";
import SaveDialogLayout from "~/components/DialogLayout/SaveDialogLayout";
import { required, Text, Note } from "~/components/form";
import EnvironmentTypeDialogLayout from "./EnvironmentTypeDialogLayout";
interface AddEnvironmentsDialogContentProps {
    saveDone: (environment: {
        Id: string;
    }) => Promise<void>;
}
interface AddEnvironmentsDialogContentPropsInternal extends AddEnvironmentsDialogContentProps {
    dispatchAction: AnalyticActionDispatcher;
    trackAction: AnalyticTrackedActionDispatcher;
    isEphemeralEnvironmentsEnabled: boolean;
}
interface AddEnvironmentsDialogContentState extends DataBaseComponentState {
    name: string;
    description: string;
    useGuidedFailure: boolean;
    newEnvironmentId: string;
    type?: EnvironmentType;
}
class AddEnvironmentsDialogContent extends DataBaseComponent<AddEnvironmentsDialogContentPropsInternal, AddEnvironmentsDialogContentState> {
    constructor(props: AddEnvironmentsDialogContentPropsInternal) {
        super(props);
        this.state = {
            name: "",
            description: "",
            useGuidedFailure: false,
            newEnvironmentId: "",
            type: this.props.isEphemeralEnvironmentsEnabled ? undefined : "Static",
        };
    }
    async saveStaticEnvironment() {
        return this.doBusyTask(async () => {
            const ev: ActionEvent = {
                action: Action.Save,
                resource: "Environment",
            };
            await this.props.trackAction("Save Environment", ev, async () => {
                const result = await repository.Environments.create({
                    Name: this.state.name,
                    Description: this.state.description,
                    UseGuidedFailure: this.state.useGuidedFailure,
                    AllowDynamicInfrastructure: true,
                    SortOrder: -1, // API automatically assigns this value if <= 0.
                    ExtensionSettings: [],
                }, {});
                this.setState({
                    newEnvironmentId: result.Id,
                });
                await this.props.saveDone(result);
            });
        });
    }
    async saveParentEnvironment() {
        return this.doBusyTask(async () => {
            const ev: ActionEvent = {
                action: Action.Save,
                resource: "ParentEnvironment",
            };
            await this.props.trackAction("Save Environment", ev, async () => {
                const result = await repository.ParentEnvironments.create({
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    spaceId: repository.spaceId!,
                    name: this.state.name,
                    description: this.state.description,
                });
                this.setState({
                    newEnvironmentId: result.Id,
                });
                await this.props.saveDone(result);
            });
        });
    }
    private renderSaveDialogLayout(options: {
        title: string;
        onSave: () => Promise<boolean>;
        examples: string[];
    }) {
        return (<SaveDialogLayout title={options.title} busy={this.state.busy} errors={this.errors} savePermission={{ permission: Permission.EnvironmentCreate, environment: "*" }} onSaveClick={options.onSave} onCancelClick={() => {
                this.props.dispatchAction("Cancel Adding Environment", {
                    resource: "Environment",
                    action: Action.Cancel,
                });
                return true; // Return true so the dialog will auto-close still.
            }}>
                <Text label="New environment name" value={this.state.name} onChange={this.handleNameChange} autoFocus={true} validate={required("Please enter an environment name")}/>
                <Note>
                    Examples include:{" "}
                    {options.examples.map((example, index, arr) => (<React.Fragment key={example}>
                            <a href="#" onClick={(e) => this.setEnvironmentName(e, example)}>
                                {example}
                            </a>
                            {index < arr.length - 2 ? ", " : index === arr.length - 2 ? " and " : ""}
                        </React.Fragment>))}
                </Note>
            </SaveDialogLayout>);
    }
    render() {
        if (this.state.type === undefined) {
            return (<EnvironmentTypeDialogLayout onSelectType={(selectedType) => {
                    this.setState({ type: selectedType });
                }}/>);
        }
        if (this.state.type === "Static") {
            return this.renderSaveDialogLayout({
                title: "Add Environment",
                onSave: () => this.saveStaticEnvironment(),
                examples: ["Development", "Test", "Production"],
            });
        }
        if (this.state.type === "Parent") {
            return this.renderSaveDialogLayout({
                title: "Add Parent Environment",
                onSave: () => this.saveParentEnvironment(),
                examples: ["QA Testing", "Pull Requests"],
            });
        }
    }
    private setEnvironmentName(e: React.MouseEvent<Element, MouseEvent>, name: string) {
        e.preventDefault();
        this.setState({ name });
    }
    private handleNameChange = (value: string) => {
        this.setState({ name: value });
    };
    static displayName = "AddEnvironmentsDialogContent";
}
export function AddEnvironmentsDialog(props: AddEnvironmentsDialogContentProps) {
    const dispatchAction = useAnalyticActionDispatch();
    const trackAction = useAnalyticTrackedActionDispatch();
    const isEphemeralEnvironmentsEnabled = useEphemeralEnvironmentsEnabled();
    return <AddEnvironmentsDialogContent {...props} dispatchAction={dispatchAction} trackAction={trackAction} isEphemeralEnvironmentsEnabled={isEphemeralEnvironmentsEnabled}/>;
}
